import { useMemo } from 'react';
import { Stack, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import { ProfileViewRenderer } from './ProfileViewRenderer';

interface WorkProfileSectionProps {
  roleName: string;
  /**
   * Label for the record-access row — "Teams" on FR, "Nodes" on a node-based
   * client (DSCO-5543). Derived from `activeDimension.name` (pluralized) by
   * `useProfilePage` and passed in already-resolved, so this stays a
   * presentational component. Backend-driven, hence not `t()`-wrapped here or
   * there.
   */
  accessDimensionLabel: string;
  /** Comma-joined display names of the actor's record-access tags. */
  accessDimensionNames: string;
  skillNames: string;
  /** Plain ISO-8601 date; the backend owns the per-profile temporal conversion (§5.1.1). */
  joiningDate: string | null;
  locale?: string;
}

const displayDate = (
  localDate: string | null | undefined,
  locale?: string
): string | null => {
  if (!localDate) {
    return null;
  }
  try {
    const [y, m, d] = localDate.split('-').map(Number);
    return new Intl.DateTimeFormat(locale ?? 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(y, m - 1, d));
  } catch {
    return localDate;
  }
};

export function WorkProfileSection({
  roleName,
  accessDimensionLabel,
  accessDimensionNames,
  skillNames,
  joiningDate,
  locale,
}: Readonly<WorkProfileSectionProps>) {
  const { t } = useTranslation();

  const fields = useMemo(
    () => [
      { label: t('dashboard.userDetails.role', 'Role'), value: roleName },
      { label: accessDimensionLabel, value: accessDimensionNames },
      { label: t('dashboard.userDetails.skills', 'Skills'), value: skillNames },
      {
        label: t('dashboard.userDetails.joiningDate', 'Joining Date'),
        value: displayDate(joiningDate, locale),
      },
    ],
    [
      t,
      roleName,
      accessDimensionLabel,
      accessDimensionNames,
      skillNames,
      joiningDate,
      locale,
    ]
  );

  return (
    <div
      className="rounded-md border border-border bg-card"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'work-profile-section',
        page: 'account',
      })}
    >
      <Stack gap={3} className="p-8">
        <Stack className="gap-1">
          <Typography variant="h6" fontWeight="semibold">
            {t('dashboard.userDetails.workProfile', 'Work Profile')}
          </Typography>
          <Typography variant="body2" color="muted">
            {t(
              'dashboard.userDetails.workProfileDescription',
              'These details are managed by your administrator.'
            )}
          </Typography>
        </Stack>

        <ProfileViewRenderer fields={fields} columns={2} />
      </Stack>
    </div>
  );
}
